import { useEffect, useState, useRef } from "react";
import InvoiceTable from "Pages/AdminInterface/clients/client/invoices/components/InvoiceTable";
import dayjs from "dayjs";
import { getMyInvoices } from "API/myPages";
import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import DateWithClear from "Components/UI/Form/DateWithClear";
import { openStatusOptions } from "utils/options/openStatusOptions";
import { SUB_STATUS } from "utils/options/SUB_STATUS";
import CreateInvoiceModal from "Components/UI/Modals/CreateInvoiceModal";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import createFilters from "utils/invoicesFilterParser";

const initialFormState = {
  ClientId: null,
  ClientCreditFee: "",
  ClientServiceFee: "",
  ClientDirectPayment: "",
  CreateTime: "",
  CurrencyCode: "SEK",
  EndCustomerId: null,
  DueDate: null,
  ExternalInvoiceNo: "",
  ExternalReference: "",
  FileId: "",
  InvoiceId: "",
  OriginalDeadlineDate: "",
  OriginalFfdDate: "",
  Pm1WaitCreateDate: "",
  OurReference: "",
  ParseMessage: "",
  ParseStatus: "",
  Pm1Fee: "",
  Pm1FfdDate: "",
  Pm2WaitCreateDate: null,
  Pm2Fee: "",
  CollectionWaitCreateDate: "",
  Pm2FfdDate: "",
  Price: 0,
  Regress: -1,
  Service: "purchase",
  SystemStatus: "",
  Text: "",
  OCR: null,
  EndCustomerNumber: "",
};

const removeStatus = ["Utkast", "Matchningsförslag", "Borttagen"];

export default function MyPageInvoices({ endcustomers, client }) {
  const [invoices, setInvoices] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openStatus, setOpenStatus] = useState(-1);
  const [subStatus, setSubStatus] = useState(-1);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState(null);
  const [order, setOrder] = useState({ Column: "InvoiceDate", Order: 0 });
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  const { openError } = usePopupNotificationContext();

  const prevFilters = useRef({
    startDate,
    endDate,
    openStatus,
    subStatus,
    order,
  });

  useEffect(() => {
    handleInputChange("ClientId", client.Id);
  }, [client]);

  const handleInputChange = (field, newVal) => {
    setFormData({ ...formData, [field]: newVal });
  };

  const getMyPageInvoicesReq = async (
    force = false,
    searchTerm = null,
    pageOverride = null
  ) => {
    setLoadingInvoices(true);
    if (searchTerm) setSearchTerm(searchTerm);
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        searchTerm: searchTerm,
      });

      const newInvoices = await getMyInvoices({
        Filters: filters,
        Order: order,
        Page: pageOverride !== null ? pageOverride : currentPage,
        PageSize: subStatus === -1 ? rowsPerPage : 0,
      });

      setTotalRows(newInvoices.Count);

      if (force) {
        setInvoices(newInvoices.Rows);
      } else {
        const invoiceMap = new Map(
          newInvoices.Rows.map((invoice) => [invoice.Id, invoice])
        );
        const mergedInvoices = [
          ...new Map(
            [
              ...invoices.filter((invoice) => !invoiceMap.has(invoice.Id)),
              ...newInvoices.Rows,
            ].map((invoice) => [invoice.Id, invoice])
          ).values(),
        ];

        setInvoices(mergedInvoices);
      }
    } catch (err) {
      openError("Ett fel uppstod", 3000);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const onDownload = async () => {
    try {
      let filters = createFilters({
        startDate: startDate,
        endDate: endDate,
        openStatus: openStatus,
        subStatus: subStatus,
        searchTerm: null,
      });
      const invoices = await getMyInvoices({
        Filters: filters,
        Order: {
          Column: "InvoiceDate",
          Order: 1,
        },
        Page: 0,
        PageSize: 0,
      });
      return invoices;
    } catch (err) {
      openError("Ett fel uppstod", 3000);
    }
  };

  useEffect(() => {
    const filtersChanged =
      prevFilters.current.startDate !== startDate ||
      prevFilters.current.endDate !== endDate ||
      prevFilters.current.openStatus !== openStatus ||
      prevFilters.current.subStatus !== subStatus ||
      prevFilters.current.order.Column !== order.Column ||
      prevFilters.current.order.Order !== order.Order;

    const getInvoicesReq = async () => {
      try {
        if (filtersChanged) {
          await getMyPageInvoicesReq(true, null, 0);
          setCurrentPage(0);
        } else {
          await getMyPageInvoicesReq(false);
        }
      } catch (err) {
        openError("Någonting gick fel vid hämtning av fakturor", 3000);
      }
    };

    prevFilters.current = {
      startDate,
      endDate,
      openStatus,
      subStatus,
      order,
    };

    getInvoicesReq();
  }, [
    startDate,
    endDate,
    rowsPerPage,
    currentPage,
    openStatus,
    subStatus,
    order,
  ]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <DateWithClear
            label="Från"
            value={startDate}
            onChange={(newVal) => {
              setStartDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <DateWithClear
            label="Till"
            value={endDate}
            onChange={(newVal) => {
              setEndDate(dayjs(newVal).format("YYYY-MM-DD 00:00:00"));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Stängd/Öppen</InputLabel>
            <Select
              name="ClosedDate"
              value={openStatus}
              onChange={(e) => setOpenStatus(e.target.value)}
              label="ClosedDate"
              size="small"
            >
              {openStatusOptions.map((options) => (
                <MenuItem key={options.Value} value={options.Value}>
                  {options.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Substatus</InputLabel>
            <Select
              name="Substatus"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value)}
              label="Substatus"
              size="small"
            >
              {SUB_STATUS.map((options) => (
                <MenuItem key={options.value} value={options.value}>
                  {options.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <CreateInvoiceModal
        handleClose={() => setCreateInvoiceModalOpen(false)}
        isOpen={createInvoiceModalOpen}
        client={client}
        endCustomers={endcustomers}
      />
      <InvoiceTable
        invoices={invoices}
        openCreateInvoice={() => setCreateInvoiceModalOpen(true)}
        onSearch={(searchTerm) => getMyPageInvoicesReq(true, searchTerm)}
        onChangePage={(pageNr) => {
          setLoadingInvoices(true);
          setCurrentPage(pageNr);
        }}
        onChangeRowsPerPage={(invoiceNumber) => {
          setRowsPerPage(invoiceNumber);
          setCurrentPage(0);
        }}
        onChangeSort={(column, order) => {
          setLoadingInvoices(true);
          setOrder({ Column: column, Order: order });
        }}
        sortOrder={order}
        getAllOnDownload={onDownload}
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        invoicesLoading={loadingInvoices}
        onUpdate={() => getMyPageInvoicesReq(false)}
      />
    </>
  );
}
